/**
 * ISSUE-1361. "Who reacted" for one message, after v1's
 * `reaction_preview_sheet.dart`: an `All` tab and one tab per emoji, each
 * listing the fans who reacted (picture or initials, name, and the emoji on
 * the All tab), newest first.
 *
 * The tab labels and counts come from the bubble's own `/message/list`
 * reactions; the people come from `/reaction/list`, which already leaves out
 * anyone in a block with the viewer and names a deleted account "Deleted
 * account" (messaging overview rule 33). Each tab pages with `Show more`:
 * every page is its own read, keyed by the cursor the page before returned.
 */
import { useReactionList } from "@sportsmet/messaging-sdk/private";
import { Button, Dialog, Skeleton, Tabs } from "@sportsmet/ui";
import { type ReactNode, useEffect, useState } from "react";

import { extractErrorCode } from "../../errors/apiErrorBus";
import { ChatAvatar } from "./ChatAvatar";
import {
  buildReactorsTabs,
  initialReactorsTab,
  REACTORS_DIALOG_TITLE,
  REACTORS_EMPTY,
  REACTORS_FAILED,
  REACTORS_PAGE_LIMIT,
  REACTORS_SHOW_MORE_LABEL,
  REACTORS_YOU_SUFFIX,
} from "./reactorsLogic";

function ReactorsPage({
  messageId,
  emoji,
  cursor,
  isFirstPage,
  isLastLoadedPage,
  showEmoji,
  onShowMore,
}: {
  messageId: string;
  emoji: string | null;
  cursor: string | null;
  isFirstPage: boolean;
  isLastLoadedPage: boolean;
  showEmoji: boolean;
  onShowMore: (nextCursor: string) => void;
}): ReactNode {
  const read = useReactionList({
    messageId,
    limit: REACTORS_PAGE_LIMIT,
    ...(emoji !== null ? { emoji } : {}),
    ...(cursor !== null ? { cursor } : {}),
  });

  if (read.isLoading) {
    return (
      <div className="flex flex-col gap-2" data-testid="reactors-loading">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }
  if (read.isError || extractErrorCode(read.data) !== null) {
    return (
      <p className="text-body text-ink-secondary" data-testid="reactors-failed">
        {REACTORS_FAILED}
      </p>
    );
  }

  const items = read.data?.data?.items ?? [];
  const nextCursor = read.data?.data?.nextCursor ?? null;
  if (isFirstPage && items.length === 0) {
    return (
      <p className="text-body text-ink-secondary" data-testid="reactors-empty">
        {REACTORS_EMPTY}
      </p>
    );
  }

  return (
    <>
      {items.map((item) => (
        <li
          key={`${item.userId}-${item.emoji}`}
          data-testid={`reactor-${item.userId}`}
          className="flex min-h-touch-target items-center gap-3 py-1"
        >
          <ChatAvatar
            title={item.displayName}
            photoUrl={item.avatarUrl ?? null}
            size="sm"
          />
          <span className="min-w-0 flex-1 truncate text-body text-ink">
            {item.displayName}
            {item.isMe ? (
              <span className="text-ink-secondary"> {REACTORS_YOU_SUFFIX}</span>
            ) : null}
          </span>
          {showEmoji ? (
            <span className="text-h2" aria-label={`Reacted ${item.emoji}`}>
              {item.emoji}
            </span>
          ) : null}
        </li>
      ))}
      {isLastLoadedPage && nextCursor !== null ? (
        <li className="pt-2">
          <Button
            variant="ghost"
            data-testid="reactors-show-more"
            onClick={() => {
              onShowMore(nextCursor);
            }}
          >
            {REACTORS_SHOW_MORE_LABEL}
          </Button>
        </li>
      ) : null}
    </>
  );
}

function ReactorsTabList({
  messageId,
  emoji,
}: {
  messageId: string;
  emoji: string | null;
}): ReactNode {
  // Page cursors loaded so far for this tab. The first page has none.
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  return (
    <ul className="flex flex-col" data-testid="reactors-list">
      {cursors.map((cursor, index) => (
        <ReactorsPage
          key={cursor ?? "first"}
          messageId={messageId}
          emoji={emoji}
          cursor={cursor}
          isFirstPage={index === 0}
          isLastLoadedPage={index === cursors.length - 1}
          showEmoji={emoji === null}
          onShowMore={(next) => {
            setCursors((previous) =>
              previous.includes(next) ? previous : [...previous, next],
            );
          }}
        />
      ))}
    </ul>
  );
}

export function ReactorsDialog({
  open,
  onOpenChange,
  messageId,
  reactions,
  initialEmoji,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  messageId: string | null;
  reactions: readonly { emoji: string; count: number }[];
  /** The chip that was clicked; null opens the All tab. */
  initialEmoji: string | null;
}): ReactNode {
  const tabs = buildReactorsTabs(reactions);
  const [tab, setTab] = useState(() => initialReactorsTab(tabs, initialEmoji));

  // Every open starts on the chip that was clicked. `tabs` is rebuilt per
  // render from `reactions`, so it stays out of the list: the open edge is
  // what matters.
  useEffect(() => {
    if (open) setTab(initialReactorsTab(tabs, initialEmoji));
  }, [open, messageId, initialEmoji]);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={REACTORS_DIALOG_TITLE}
      autoFocus={false}
    >
      {messageId === null ? null : (
        <Tabs
          label={REACTORS_DIALOG_TITLE}
          value={tab}
          onValueChange={setTab}
          items={tabs.map((item) => ({
            value: item.value,
            label: item.label,
            badge: item.badge,
            content: (
              <ReactorsTabList messageId={messageId} emoji={item.emoji} />
            ),
          }))}
        />
      )}
    </Dialog>
  );
}
